import { debounce, groupBy, uniqBy } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

// MUI
import { HomeRounded, MenuRounded, RefreshRounded, SearchRounded } from '@mui/icons-material';
import {
  Box,
  Button,
  Grid,
  IconButton,
  Paper,
  Slide,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

// common
import useHookQuery from 'common/hook/useHookQuery';

// screen
import SelectCustom from 'modules/map/SelectorCustom';
import { ClientModel, CustomerTypeOptions, FilterModel } from 'modules/map/types';
import SchoolCardInfo from 'modules/map/components/SchoolCardInfo';

interface Props {
  map: vietmapgl.Map | null;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  filter: FilterModel;
  setFilter: React.Dispatch<React.SetStateAction<FilterModel>>;
  filterData: ClientModel[];
  data: ClientModel[];
  focusData?: ClientModel;
  setFocusData: React.Dispatch<React.SetStateAction<ClientModel | undefined>>;
}

function ListSchool(props: Props) {
  const { map, open, setOpen, filter, setFilter, data, filterData, focusData, setFocusData } =
    props;

  /**
   * Từ khoá đang gõ, giữ RIÊNG khỏi `filter.search`.
   *
   * Trước đây ô này không kiểm soát và mang `key={filter.search}`. Key đổi theo giá trị nên mỗi
   * lần state cập nhật là React HUỶ ô input cũ và dựng ô mới. Trên máy tính `autoFocus` focus lại
   * nên gần như không ai thấy; trên màn cảm ứng (Android TV) focus bằng lệnh KHÔNG mở lại được bàn
   * phím ảo — gõ một ký tự là bàn phím tắt, không gõ tiếp được.
   *
   * Tách hai state: ô input cập nhật tức thì (không bao giờ bị dựng lại), còn `filter.search` —
   * thứ kéo theo cả việc lọc lại danh sách và vẽ lại bản đồ — vẫn được hoãn 300ms như cũ.
   */
  const [tuKhoa, setTuKhoa] = useState(filter.search ?? '');

  // Đồng bộ ngược khi bộ lọc bị xoá từ bên ngoài (nút làm mới, nút xoá lọc gọi setFilter({})).
  // Chính nhu cầu này là lý do trước đây phải dùng `key` để ép dựng lại ô input.
  useEffect(() => {
    setTuKhoa(filter.search ?? '');
  }, [filter.search]);

  // debounce PHẢI tạo một lần. Trước đây tạo mới ngay trong onChange nên mỗi lần gõ lại là một hàm
  // khác, mỗi hàm tự hẹn giờ riêng — hoãn gần như không có tác dụng.
  const hoanCapNhatTimKiem = useMemo(
    () => debounce((giaTri: string) => setFilter((old) => ({ ...old, search: giaTri })), 300),
    [setFilter]
  );
  useEffect(() => () => hoanCapNhatTimKiem.cancel(), [hoanCapNhatTimKiem]);


  const { isDesktop } = useHookQuery();
  const intl = useIntl();

  const LocationOptions = useMemo(() => {
    return groupBy(data, 'city') as Record<string, ClientModel[]>;
  }, [data]);

  return (
    <>
      <Tooltip title={intl.formatMessage({ id: 'map.openFilter' })}>
        <IconButton
          color="primary"
          onClick={() => {
            setOpen(true);
          }}
          sx={{
            bgcolor: 'background.paper',
            position: 'absolute',
            height: 'fit-content',
            width: 'fit-content',
            top: 0,
            left: 0,
            '&:hover': { bgcolor: 'background.paper' },
          }}
        >
          <SearchRounded />
        </IconButton>
      </Tooltip>
      <Slide direction="right" in={open}>
        <Paper
          sx={(theme) => ({
            width: 320,
            [theme.breakpoints.up('laptop')]: {
              width: 360,
            },
            [theme.breakpoints.up('desktop')]: {
              width: 420,
            },
          })}
          className="flex flex-col h-full overflow-hidden absolute z-20"
        >
          <Box p={1} pb={0}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <IconButton color="primary">
                <HomeRounded />
              </IconButton>
              <Typography variant="subtitle1" flex={1}>
                {'Bộ lọc'}
              </Typography>
              {Object.values(filter).length > 0 && !isDesktop && (
                <Tooltip title={intl.formatMessage({ id: 'map.clearFilter' })}>
                  <IconButton color="primary" onClick={() => setFilter({})} sx={{ width: 40 }}>
                    <RefreshRounded />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title={intl.formatMessage({ id: 'map.closeFilter' })}>
                <IconButton
                  color="primary"
                  onClick={() => {
                    setOpen(false);
                  }}
                >
                  <MenuRounded />
                </IconButton>
              </Tooltip>
            </Box>
            <Grid container spacing={1}>
              <Grid item mobile={12}>
                <TextField
                  autoFocus
                  value={tuKhoa}
                  onChange={(val) => {
                    setTuKhoa(val.target.value);
                    hoanCapNhatTimKiem(val.target.value);
                  }}
                  placeholder={intl.formatMessage({
                    id: 'map.searchByCustomerName',
                  })}
                  fullWidth
                  inputProps={{
                    style: { lineHeight: 2 },
                  }}
                />
              </Grid>
              <Grid item mobile={12} desktop={6}>
                <SelectCustom
                  value={filter.cityId}
                  onChange={(val) => {
                    setFilter((old) => ({
                      ...old,
                      cityId: val,
                      wardId: '',
                    }));
                  }}
                  placeholder="Tỉnh/Thành phố"
                  options={Object.keys(LocationOptions).map((v) => ({
                    id: v,
                    name: v,
                  }))}
                />
              </Grid>
              <Grid item mobile={12} desktop={6}>
                <SelectCustom
                  value={filter.wardId}
                  onChange={(val) => {
                    setFilter((old) => ({
                      ...old,
                      wardId: val,
                    }));
                  }}
                  placeholder="Phường/xã"
                  options={
                    filter.cityId
                      ? uniqBy(
                          LocationOptions[filter.cityId].map((v) => ({
                            id: v.wardName,
                            name: v.wardName,
                          })),
                          'id'
                        )
                      : []
                  }
                />
              </Grid>
              <Grid item mobile={12} desktop={6}>
                <SelectCustom
                  value={filter.customerType}
                  onChange={(val) => {
                    setFilter((old) => ({
                      ...old,
                      customerType: val,
                    }));
                  }}
                  placeholder="Phân loại KH"
                  options={CustomerTypeOptions}
                />
              </Grid>
              {isDesktop && (
                <Grid item mobile={12} desktop={6}>
                  <Button
                    variant="outlined"
                    color="primary"
                    fullWidth
                    onClick={() => setFilter({})}
                    startIcon={<RefreshRounded />}
                  >
                    {'Xoá'}
                  </Button>
                </Grid>
              )}
            </Grid>
          </Box>
          <Typography variant="body1" p={1} color="textSecondary">
            {filterData.length + ' khách hàng'}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              p: 1,
              pt: 0.4,
              overflow: 'auto',
            }}
          >
            {filterData?.length > 0 ? (
              filterData.map((value, index) => {
                return (
                  <SchoolCardInfo
                    key={index}
                    data={value}
                    isActive={focusData?.operationCode === value.operationCode}
                    onClick={() => {
                      setFocusData(value);
                      map?.flyTo({
                        center: [value.lon, value.lat],
                        zoom: 15,
                        duration: 1000,
                      });
                    }}
                  />
                );
              })
            ) : (
              <Typography variant="body1">{'Không tìm thấy kết quả phù hợp'}</Typography>
            )}
          </Box>
        </Paper>
      </Slide>
    </>
  );
}

export default ListSchool;
